import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import {
  CalendarIcon,
  UserIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  EyeSlashIcon,
  CalendarDaysIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import { isTrainer, isBoard } from '../utils/roleUtils';
import ConfirmationModal from '../components/ConfirmationModal';
import EventCard from '../components/EventCardNew';
import { StatCard, StatGroup } from '../components/StatCard';

interface Event {
  id: number;
  title: string;
  start: string;
  start_time: string;
  end_time: string;
  beschreibung?: string;
  location?: string;
  creator_name: string;
  creator_image?: string;
  creator_image_updated?: number;
  creator: string;
  cotrainer?: string;
  cotrainer_name?: string;
  cotrainer_image?: string;
  cotrainer_image_updated?: number;
  max_teilnehmer?: number;
  participantCount: number;
  unique_id: string;
  farbe: string;
  serie: boolean;
  participants?: string[];
}

interface EventParticipant {
  mg_nr: string;
  name: string;
  vorname: string;
  email: string;
  telefon: string;
  member_type: 'member' | 'guest';
  guest_card_id?: string;
  booking_date: string;
}

interface CancellationPeriods {
  members: string | number;
  guests: number;
}

interface EventsResponse {
  eventsCreated: Event[];
  eventsBooked: Event[];
}

interface EventGroup {
  unique_id: string;
  title: string;
  events: Event[];
  creator_name: string;
  isExpanded: boolean;
}

const MyBookings: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const [eventsCreated, setEventsCreated] = useState<Event[]>([]);
  const [eventsBooked, setEventsBooked] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);
  const [activeTab, setActiveTab] = useState<'created' | 'booked'>('booked');
  const [eventGroups, setEventGroups] = useState<Map<string, EventGroup>>(new Map());
  const [participantsData, setParticipantsData] = useState<Map<number, EventParticipant[]>>(new Map());
  const [showParticipants, setShowParticipants] = useState<Set<number>>(new Set());
  const [loadingParticipants, setLoadingParticipants] = useState<Set<number>>(new Set());
  const [cancellationPeriods, setCancellationPeriods] = useState<CancellationPeriods | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
    variant: 'single' | 'two';
    onConfirm?: () => void;
    onClose?: () => void;
  }>({
    visible: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });

  const isTrainerUser = isTrainer(user) || isBoard(user);

  const fetchCancellationPeriods = async () => {
    try {
      const response = await apiClient.get('/api/config/cancellation_periods');
      if (response.data.success) {
        setCancellationPeriods(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching cancellation periods:', error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const url = showPast
        ? '/api/calendar/user-events-calendar/unknown?past=true'
        : '/api/calendar/user-events-calendar/unknown';

      const response = await apiClient.get(url);
      const data: EventsResponse = response.data;

      setEventsCreated(data.eventsCreated || []);
      setEventsBooked(data.eventsBooked || []);
      if (!data.eventsCreated?.length) {
        setActiveTab('booked');
      }
      processEventGroups([...data.eventsCreated, ...data.eventsBooked]);
    } catch (error) {
      console.error('Error fetching events:', error);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Termine konnten nicht geladen werden.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  const processEventGroups = (allEvents: Event[]) => {
    const groups = new Map<string, EventGroup>();
    const expandState = JSON.parse(localStorage.getItem('eventGroups_expanded') || '{}');

    allEvents.forEach(event => {
      if (event.serie && event.unique_id) {
        if (!groups.has(event.unique_id)) {
          groups.set(event.unique_id, {
            unique_id: event.unique_id,
            title: event.title,
            events: [],
            creator_name: event.creator_name,
            isExpanded: expandState[event.unique_id] || false
          });
        }
        groups.get(event.unique_id)!.events.push(event);
      }
    });

    setEventGroups(groups);
  };

  const toggleEventGroup = (uniqueId: string) => {
    const newGroups = new Map(eventGroups);
    const group = newGroups.get(uniqueId);
    if (group) {
      group.isExpanded = !group.isExpanded;
      newGroups.set(uniqueId, group);
      setEventGroups(newGroups);

      const expandState = JSON.parse(localStorage.getItem('eventGroups_expanded') || '{}');
      expandState[uniqueId] = group.isExpanded;
      localStorage.setItem('eventGroups_expanded', JSON.stringify(expandState));
    }
  };

  const fetchParticipants = async (eventId: number) => {
    if (participantsData.has(eventId)) return;

    setLoadingParticipants(prev => new Set(prev).add(eventId));

    try {
      const response = await apiClient.get(`/api/calendar/${eventId}/participants`);
      if (response.data.success) {
        setParticipantsData(prev => new Map(prev).set(eventId, response.data.participants));
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
    } finally {
      setLoadingParticipants(prev => {
        const newSet = new Set(prev);
        newSet.delete(eventId);
        return newSet;
      });
    }
  };

  const toggleParticipants = async (eventId: number) => {
    const newShowParticipants = new Set(showParticipants);

    if (newShowParticipants.has(eventId)) {
      newShowParticipants.delete(eventId);
    } else {
      newShowParticipants.add(eventId);
      await fetchParticipants(eventId);
    }

    setShowParticipants(newShowParticipants);
  };

  const canCancelEvent = (event: Event, isTrainer: boolean = false): { allowed: boolean; reason?: string } => {
    if (!cancellationPeriods) return { allowed: false, reason: 'Stornierungsregeln werden geladen...' };

    const eventDate = new Date(`${event.start}T${event.start_time}`);
    const now = new Date();
    const hoursDiff = (eventDate.getTime() - now.getTime()) / (1000 * 60 * 60);

    if (hoursDiff < 0) {
      return isTrainer
        ? { allowed: true, reason: 'Event ist bereits vorbei, als Trainer stornieren?' }
        : { allowed: false, reason: 'Event ist bereits vorbei' };
    }

    const memberPeriod = cancellationPeriods.members;

    if (memberPeriod === "unbegrenzt") {
      return { allowed: true };
    }

    if (typeof memberPeriod === 'number' && hoursDiff < memberPeriod) {
      return isTrainer
        ? { allowed: true, reason: 'Stornierungsfrist überschritten, als Trainer stornieren?' }
        : { allowed: false, reason: `Stornierung nur bis ${memberPeriod}h vor dem Event möglich` };
    }

    return { allowed: true };
  };

  const cancelBooking = async (eventId: number) => {
    try {
      const response = await apiClient.delete(`/api/members/cancel-event/${eventId}`);
      if (response.data.success) {
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Termin wurde erfolgreich storniert.',
          variant: 'single',
          onClose: () => {
            fetchEvents();
          }
        });
        fetchEvents();
      } else {
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: response.data.message || 'Stornierung fehlgeschlagen.',
          variant: 'single'
        });
      }
    } catch (error: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: error.response?.data?.message || 'Stornierung fehlgeschlagen.',
        variant: 'single'
      });
    }
  };

  const removeParticipant = async (eventId: number, participant: EventParticipant) => {
    try {
      const cardId = participant.guest_card_id || '';
      const response = await apiClient.delete(
        `/api/members/cancel-event/${eventId}?member_id=${participant.mg_nr}&card_id=${cardId}`
      );

      if (response.data.success) {
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Teilnehmer wurde erfolgreich entfernt.',
          variant: 'single'
        });

        setParticipantsData(prev => {
          const newMap = new Map(prev);
          newMap.delete(eventId);
          return newMap;
        });
        fetchEvents();
      } else {
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: response.data.message || 'Teilnehmer konnte nicht entfernt werden.',
          variant: 'single'
        });
      }
    } catch (error: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: error.response?.data?.message || 'Teilnehmer konnte nicht entfernt werden.',
        variant: 'single'
      });
    }
  };

  const handleCancelConfirm = (event: Event, asTrainer = false) => {
    const cancelCheck = canCancelEvent(event, asTrainer);

    if (!cancelCheck.allowed && !asTrainer) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Stornierung nicht möglich',
        message: cancelCheck.reason || 'Stornierung nicht erlaubt.',
        variant: 'single'
      });
      return;
    }

    const message = cancelCheck.reason || 'Möchten Sie diesen Termin **wirklich stornieren**?';

    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Termin stornieren',
      message,
      variant: 'two',
      onConfirm: () => cancelBooking(event.id)
    });
  };

  const handleParticipantRemoval = (eventId: number, participant: EventParticipant, event: Event) => {
    const cancelCheck = canCancelEvent(event, true);

    const message = cancelCheck.reason
      ? `${cancelCheck.reason} Teilnehmer: ${participant.vorname} ${participant.name}`
      : `Möchten Sie ${participant.vorname} ${participant.name} wirklich von diesem Termin entfernen?`;

    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Teilnehmer entfernen',
      message,
      variant: 'two',
      onConfirm: () => removeParticipant(eventId, participant)
    });
  };

  const formatEventDate = (dateStr: string, startTime: string) => {
    const date = new Date(`${dateStr}T${startTime}`);
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: 'short'
    });
  };

  const getNextEvent = (events: Event[]) => {
    const futureEvents = events.filter(event => !isEventPast(event))
      .sort((a, b) => {
        const dateA = new Date(`${a.start}T${a.start_time}`);
        const dateB = new Date(`${b.start}T${b.start_time}`);
        return dateA.getTime() - dateB.getTime();
      });

    return futureEvents[0] || null;
  };

  const isEventPast = (event: Event) => {
    const now = new Date();

    if (event.end_time) {
      const eventEndDateTime = new Date(`${event.start}T${event.end_time}`);
      return eventEndDateTime < now;
    }

    const eventStartDateTime = new Date(`${event.start}T${event.start_time}`);
    return eventStartDateTime < now;
  };

  const MyBookingsEventCard: React.FC<{ event: Event; isNext?: boolean; showActions?: boolean; eventType: 'created' | 'booked' }> = ({
    event,
    isNext = false,
    showActions = true,
    eventType
  }) => {
    const loadingParticipantData = loadingParticipants.has(event.id);

    return (
      <EventCard
        event={event}
        isNext={isNext}
        showCancelButton={eventType === 'booked' && showActions}
        showParticipantsButton={eventType === 'created' && showActions}
        participantCount={event.participantCount}
        loadingParticipants={loadingParticipantData}
        onCancel={() => handleCancelConfirm(event)}
        onToggleParticipants={() => toggleParticipants(event.id)}
        BASE_URL={BASE_URL}
        isGuestCardStyle={false}
      />
    );
  };

  const EventGroupCard: React.FC<{ group: EventGroup; eventType: 'created' | 'booked' }> = ({ group, eventType }) => {
    return (
      <div
        className="bg-white dark:bg-[#1c2432] rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700"
      >
        <button
          onClick={() => toggleEventGroup(group.unique_id)}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center gap-3.5">
            <div
              className="w-[52px] h-[52px] rounded-[14px] flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: group.events[0]?.farbe || '#c9f158' }}
            >
              <CalendarIcon className="h-6 w-6 text-gray-900 dark:text-white" />
            </div>
            <div className="text-left">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {group.title} (Serie)
              </h3>
              <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
                {group.events.length} Termine • {group.creator_name}
              </p>
            </div>
          </div>

          {group.isExpanded ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {group.isExpanded && (
          <div className="px-5 pb-4 space-y-3 border-t border-gray-200 dark:border-gray-700">
            <div className="pt-4">
              {group.events
                .sort((a, b) => new Date(`${a.start}T${a.start_time}`).getTime() - new Date(`${b.start}T${b.start_time}`).getTime())
                .map(event => (
                  <MyBookingsEventCard
                    key={event.id}
                    event={event}
                    eventType={eventType}
                  />
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    fetchCancellationPeriods();
    fetchEvents();
  }, [showPast]);

  const currentEvents = activeTab === 'created' ? eventsCreated : eventsBooked;
  const nextEvent = getNextEvent(currentEvents);
  const upcomingEventCount = new Set(
    [...eventsCreated, ...eventsBooked]
      .filter(event => !isEventPast(event))
      .map(event => event.id)
  ).size;

  const processedEvents = () => {
    const individualEvents: Event[] = [];
    const seriesGroups: EventGroup[] = [];

    const filteredEvents = showPast ? currentEvents : currentEvents.filter(event => !isEventPast(event));

    filteredEvents.forEach(event => {
      if (event.serie && event.unique_id && eventGroups.has(event.unique_id)) {
        const group = eventGroups.get(event.unique_id)!;
        if (!seriesGroups.find(g => g.unique_id === group.unique_id)) {
          const groupEvents = showPast ? group.events : group.events.filter(e => !isEventPast(e));
          if (groupEvents.length > 0) {
            seriesGroups.push({
              ...group,
              events: groupEvents
            });
          }
        }
      } else {
        individualEvents.push(event);
      }
    });

    return { individualEvents, seriesGroups };
  };

  const { individualEvents, seriesGroups } = processedEvents();

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Skeleton Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-6 w-44 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
        </div>
        {/* Skeleton Cards */}
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-4">
              <div className="w-14 h-14 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto w-full max-w-7xl space-y-5 pb-8">
        <header className="app-page-header overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432]">
          <div className="flex flex-col gap-5 p-5 sm:p-7 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                <CalendarDaysIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">Persönlicher Kalender</p>
                <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white sm:text-[28px]">Meine Buchungen</h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Gebuchte Termine und selbst erstellte Events auf einen Blick</p>
              </div>
            </div>
            <button
              onClick={() => setShowPast(!showPast)}
              className={`flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl px-4 text-sm font-bold transition-colors ${
                showPast
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {showPast ? <EyeSlashIcon className="h-5 w-5" /> : <ClockIcon className="h-5 w-5" />}
              {showPast ? 'Vergangene ausblenden' : 'Vergangene anzeigen'}
            </button>
          </div>
        </header>

        <StatGroup columns={3}>
          <StatCard icon={CalendarIcon} valueTone="primary" label="Gebucht" value={eventsBooked.length} />
          <StatCard icon={UserIcon} label="Eigene Events" value={eventsCreated.length} />
          <StatCard icon={ClockIcon} label="Kommend" value={upcomingEventCount} />
        </StatGroup>

        {/* Tabs - Design Guide Section 10 */}
        {isTrainerUser && eventsCreated.length > 0 && (
          <div className="flex w-full gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-700 sm:w-fit">
            <button
              onClick={() => setActiveTab('booked')}
              className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-lg px-3 text-sm font-bold transition-all sm:flex-none sm:px-5 ${
                activeTab === 'booked'
                  ? 'bg-white dark:bg-[#1c2432] text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <CalendarIcon className="h-5 w-5" />
              <span>Meine Buchungen</span>
              <span
                className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                  activeTab === 'booked'
                    ? 'bg-gray-900/10 dark:bg-white/10 text-gray-900 dark:text-white'
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-300'
                }`}
              >
                {eventsBooked.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('created')}
              className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-lg px-3 text-sm font-bold transition-all sm:flex-none sm:px-5 ${
                activeTab === 'created'
                  ? 'bg-white dark:bg-[#1c2432] text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <UserIcon className="h-5 w-5" />
              <span>Meine Events</span>
              <span
                className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                  activeTab === 'created'
                    ? 'bg-gray-900/10 dark:bg-white/10 text-gray-900 dark:text-white'
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-300'
                }`}
              >
                {eventsCreated.length}
              </span>
            </button>
          </div>
        )}

        {/* Events Section */}
        <div
          className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#1c2432] sm:p-6"
        >
          {/* Section Header */}
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary-50 dark:bg-primary-900/20">
              <CalendarIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {activeTab === 'booked' ? 'Deine Buchungen' : 'Deine Events'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {activeTab === 'booked' ? 'Gebuchte Trainingseinheiten und Events' : 'Von dir erstellte Events'}
              </p>
            </div>
          </div>

          {/* Empty State - Design Guide Section 9 */}
          {currentEvents.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 bg-gray-100 dark:bg-gray-800">
                <CalendarIcon className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Keine Termine gefunden
              </h3>
              <p className="text-[15px] text-gray-500 dark:text-gray-400 max-w-md mx-auto leading-relaxed">
                {activeTab === 'booked'
                  ? 'Du hast noch keine Termine gebucht. Schaue im Kalender nach verfügbaren Events.'
                  : 'Du hast noch keine Events erstellt.'
                }
              </p>
              {activeTab === 'booked' && (
                <a href="/calendar" className="mt-5 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 text-sm font-bold text-white transition hover:bg-primary-700">
                  <PlusIcon className="h-5 w-5" /> Termin finden
                </a>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Series Groups */}
              {seriesGroups.map(group => (
                <EventGroupCard
                  key={group.unique_id}
                  group={group}
                  eventType={activeTab}
                />
              ))}

              {/* Individual Events */}
              {individualEvents
                .sort((a, b) => new Date(`${a.start}T${a.start_time}`).getTime() - new Date(`${b.start}T${b.start_time}`).getTime())
                .map(event => (
                  <MyBookingsEventCard
                    key={event.id}
                    event={event}
                    isNext={nextEvent?.id === event.id}
                    eventType={activeTab}
                  />
                ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal(prev => ({ ...prev, visible: false }))}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal(prev => ({ ...prev, visible: false }))
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              confirmModal.onConfirm?.();
              setConfirmModal(prev => ({ ...prev, visible: false }));
            }
          }
        } : undefined}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => {
            confirmModal.onClose?.();
            setConfirmModal(prev => ({ ...prev, visible: false }));
          }
        } : undefined}
      />
    </>
  );
};

export default MyBookings;
